/**
 * The Brief tab of the Production Line detail page (production-lines): the
 * original brief (source, prompt, line, language, created) beside the
 * business context shared from the workspace. Moved verbatim out of
 * ProductionLineDetailPage.tsx.
 */

import type { Dispatch, ReactNode, SetStateAction } from 'react';

import type { MerchantProfileView } from '../../merchant/api.js';
import type { WorkspaceCopy } from '../../merchant/copy.js';
import { formatDate, type LineSummary } from '../../merchant/model.js';
import { fieldValue } from '../business-profile/BusinessProfilePage.js';
import {
  Button,
  ButtonLink,
  Card,
  DefinitionList,
  Eyebrow,
} from '../../merchant/ui/primitives.js';
import type { LineModal } from './productionLineJourney.js';

export function ProductionLineBriefTab({
  copy,
  language,
  summary,
  prompt,
  title,
  workspaceName,
  view,
  setModal,
}: {
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly summary: LineSummary | null;
  readonly prompt: string;
  readonly title: string;
  readonly workspaceName: string;
  readonly view: MerchantProfileView | null;
  readonly setModal: Dispatch<SetStateAction<LineModal>>;
}): ReactNode {
  return (
    <div role="tabpanel" className="mj-ws-g2 is-journey mj-ws-mt-20">
      <Card as="section" padding={24} className="mj-ws-col">
        <Eyebrow>{copy.line.originalBrief}</Eyebrow>
        <DefinitionList
          rows={[
            [
              copy.line.sourceUrl,
              summary?.websiteUrl ? (
                <a
                  key="u"
                  href={summary.websiteUrl}
                  dir="ltr"
                  target="_blank"
                  rel="noreferrer"
                >
                  {summary.websiteUrl}
                </a>
              ) : (
                copy.common.dash
              ),
            ],
            [copy.line.prompt, prompt || copy.common.noPromptYet],
            [copy.line.productionLine, title],
            [copy.line.videoLanguage, copy.line.videoLanguageValue],
            [
              copy.line.created,
              <span key="c" className="mj-ws-tnum">
                {summary ? formatDate(summary.createdAt, language) : copy.common.dash}
              </span>,
            ],
          ]}
        />
        <p className="mj-ws-muted mj-ws-sm">{copy.line.briefKept}</p>
        <div>
          <Button
            onClick={() => {
              setModal('brief');
            }}
          >
            {copy.line.editBrief}
          </Button>
        </div>
      </Card>
      <Card as="aside" className="mj-ws-col">
        <Eyebrow>{copy.line.businessContext}</Eyebrow>
        <p className="mj-ws-sm">{copy.line.sharedFrom(workspaceName)}</p>
        <DefinitionList
          columns="narrow"
          rows={[
            [
              copy.line.business,
              fieldValue(view?.profile?.payload.identity, workspaceName),
            ],
            [
              copy.line.offer,
              fieldValue(view?.profile?.payload.offers[0]?.title, copy.common.dash),
            ],
            [
              copy.line.geography,
              fieldValue(view?.profile?.payload.geography, copy.common.notEstablished),
            ],
          ]}
        />
        <div>
          <ButtonLink to="/business-profile" variant="quiet" small>
            {copy.line.openProfile}
          </ButtonLink>
        </div>
      </Card>
    </div>
  );
}
